import { memo, lazy, Suspense } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { resolvedObjectId } from "../../utils/connectionUserUtils";
import { AttendeeCancelRequestStyles } from "./attendeeCancelRequestStyles";
import { BACK_LINK } from "./eventAttendeesHelpers";
import EventAttendeesList from "./EventAttendeesList";
import EventAttendeesToolbar from "./EventAttendeesToolbar";
import { useEventAttendees } from "./useEventAttendees";

const ProfileModal = lazy(() => import("../common/ProfileModal"));
const SaveAttendeeNoteModal = lazy(() => import("./SaveAttendeeNoteModal"));

function EventAttendees() {
  const { t } = useTranslation();
  const s = useEventAttendees();

  if (!s.eventId) {
    return (
      <main className="page-content w-100 pb-4 m-0">
        <p className="text-danger">{t("events.invalidEvent")}</p>
        <Button
          variant="outline-secondary"
          onClick={() => s.navigate(BACK_LINK)}
        >
          {t("auth.back")}
        </Button>
      </main>
    );
  }

  return (
    <main
      className="page-content w-100 pb-4 m-0 d-flex flex-column min-vh-100"
      aria-label={t("events.attendeesAria")}
    >
      <AttendeeCancelRequestStyles />
      <EventAttendeesToolbar
        eventName={s.eventData?.name}
        showPagination={s.showPagination}
        currentPage={s.currentPage}
        filteredCount={s.filteredAndSorted.length}
        searchTerm={s.searchTerm}
        searchTargetLang={s.attendeeSearchTargetLang}
        onSearchChange={(value) => s.bindAttendeeSearch(value, s.setSearchTerm)}
        onSortChange={s.setSortOption}
        showMobileSort={s.showMobileSort}
        onMobileSortToggle={s.setShowMobileSort}
        onBack={() => s.navigate(BACK_LINK)}
      />

      <EventAttendeesList
        loading={s.loadingAttendees}
        filteredCount={s.filteredAndSorted.length}
        paginatedList={s.paginatedList}
        currentPage={s.currentPage}
        totalPages={s.totalPages}
        showPagination={s.showPagination}
        onPageChange={s.setCurrentPage}
        viewerOidNorm={s.viewerOidNorm}
        viewerOidDuplicateCount={s.viewerOidDuplicateCount}
        currentUser={s.currentUser}
        noteObjectIdsForEvent={s.noteObjectIdsForEvent}
        selectedAttendeeKeys={s.selectedAttendeeKeys}
        currentUserMembership={s.currentConnectionSource}
        explorerLimitReached={s.explorerLimitReached}
        isExplorerUser={s.isExplorer}
        onBlockedConnectionAction={s.guardConnectionAction}
        onViewProfile={s.handleViewProfile}
        onSaveNote={s.handleSaveNote}
        onMessage={s.handleMessage}
        onConnect={s.handleConnect}
        onCancelRequest={s.handleCancelRequest}
        onAccept={s.handleAccept}
        onIgnore={s.handleIgnore}
        onRemoveConnection={s.handleRemoveConnection}
        sendConnectionId={s.sendConnectionId}
        cancellingId={s.cancellingId}
        onToggleAttendeeSelect={s.handleToggleAttendeeSelect}
      />

      {s.showProfileModal && (
        <Suspense fallback={null}>
          <ProfileModal
            show={s.showProfileModal}
            onHide={() => s.setShowProfileModal(false)}
            selectedUser={s.selectedUser}
            isConnected={s.attendeesWithState.some((a) => {
              if (a.connectionState !== "connected" || !s.selectedUser) return false;
              const ao = resolvedObjectId(a.user).toLowerCase();
              const so = resolvedObjectId(s.selectedUser).toLowerCase();
              if (ao && so && ao === so) return true;
              return (
                a.user.id > 0 &&
                s.selectedUser.id > 0 &&
                a.user.id === s.selectedUser.id
              );
            })}
          />
        </Suspense>
      )}

      {s.showNoteModal && (
        <Suspense fallback={null}>
          <SaveAttendeeNoteModal
            show={s.showNoteModal}
            onHide={() => {
              s.setShowNoteModal(false);
              s.setNoteUser(null);
            }}
            eventId={s.eventId || ""}
            eventName={s.eventData?.name}
            attendee={s.noteUser}
          />
        </Suspense>
      )}

      <Modal
        show={!!s.selectedForIgnore}
        onHide={() => s.setSelectedForIgnore(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("connections.confirmIgnore")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("connections.confirmIgnoreText")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => s.setSelectedForIgnore(null)}>
            {t("connections.cancel")}
          </Button>
          <Button variant="danger" onClick={s.handleConfirmIgnore}>
            {t("connections.ignore")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={s.showRemoveModal} onHide={() => s.setShowRemoveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("connections.confirmRemoval")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("connections.confirmRemovePrompt")}{" "}
          <strong>
            {s.connectionToRemove?.firstName} {s.connectionToRemove?.lastName}
          </strong>
          ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => s.setShowRemoveModal(false)}>
            {t("connections.cancel")}
          </Button>
          <Button variant="danger" onClick={s.confirmRemove}>
            {t("connections.remove")}
          </Button>
        </Modal.Footer>
      </Modal>
      {s.limitModal}
    </main>
  );
}

export default memo(EventAttendees);
